import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import {
    MdCalendarMonth,
    MdCheckCircle,
    MdStar,
    MdRefresh,
    MdAddShoppingCart,
    MdPersonAdd,
    MdPeople,
    MdChevronRight,
    MdAccessTime,
    MdContentCut,
    MdHistory,
    MdPendingActions
} from "react-icons/md";

import { appointmentsAPI } from "services/api";
import { useAuth } from "context/AuthContext";

// --- YARDIMCI FONKSİYONLAR ---
const StarRating = ({ rating }) => (
    <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((i) => (
            <MdStar
                key={i}
                className={`text-sm ${i <= rating ? "text-amber-400" : "text-gray-200 dark:text-navy-700"}`}
            />
        ))}
    </div>
);

// --- SKELETON LOADERS ---
const DashboardSkeleton = () => (
    <div className="flex flex-col gap-5 animate-pulse mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 h-80 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
            <div className="h-80 rounded-[20px] bg-gray-200 dark:bg-navy-700" />
        </div>
    </div>
);

const StaffDashboard = () => {
    const { user } = useAuth();

    // --- STATELER ---
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [stats, setStats] = useState({
        upcomingCount: 0,
        completedCount: 0,
        totalAppointments: 0,
        avgRating: 0,
    });
    const [chartData, setChartData] = useState({
        series: [{ name: "Tamamlanan İşlem", data: [] }],
        categories: [],
    });
    const [statusData, setStatusData] = useState({ series: [0, 0], labels: ["Gelecek", "Tamamlanan"] });
    const [recentAppointments, setRecentAppointments] = useState([]);
    const [recentReviews, setRecentReviews] = useState([]);

    // --- VERİ İŞLEME MANTIĞI (FİNANS ÇIKARTILDI, İŞLEM SAYISINA ODAKLANILDI) ---
    const fetchData = useCallback(async () => {
        if (refreshing) return;
        setLoading(true);
        try {
            const response = await appointmentsAPI.getMyAppointments();
            if (response.success) {
                const apps = response.appointments || [];
                const now = new Date();

                // 1. İstatistikleri Hesapla
                const completed = apps.filter((a) => a.status === "completed");
                const upcoming = apps.filter((a) => a.status === "approved" || a.status === "pending");

                // 2. Grafik Verileri (Son 7 Ay - SADECE İŞLEM SAYISI)
                const months = [];
                const monthlyCounts = [];
                const monthNames = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

                for (let i = 6; i >= 0; i--) {
                    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                    const m = d.getMonth();
                    const y = d.getFullYear();
                    months.push(`${monthNames[m]} ${y.toString().slice(-2)}`);

                    const monthlyCompletedCount = completed.filter((a) => {
                        const ad = new Date(a.appointment_date);
                        return ad.getMonth() === m && ad.getFullYear() === y;
                    }).length;

                    monthlyCounts.push(monthlyCompletedCount);
                }

                setStats({
                    upcomingCount: upcoming.length,
                    completedCount: completed.length,
                    totalAppointments: apps.length,
                    avgRating: user?.average_rating || 0,
                });

                setChartData({
                    series: [{ name: "Aylık İşlem Sayısı", data: monthlyCounts }],
                    categories: months,
                });

                setStatusData({
                    series: [upcoming.length, completed.length],
                    labels: ["Gelecek", "Tamamlanan"],
                });

                // Tablo verileri (En son 5 yaklaşan/bekleyen randevu)
                const sortedUpcoming = upcoming
                    .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
                    .slice(0, 5);
                setRecentAppointments(sortedUpcoming);

                // Yorumlar (Sadece değerlendirmesi olanlar, en yeniler)
                const reviews = apps
                    .filter((a) => a.rating)
                    .sort((a, b) => new Date(b.rating.created_at) - new Date(a.rating.created_at))
                    .map((a) => ({
                        id: a.rating.id,
                        service: a.service?.name,
                        score: a.rating.rating,
                        comment: a.rating.comment,
                        date: a.appointment_date,
                    }))
                    .slice(0, 5);
                setRecentReviews(reviews);
            }
        } catch (err) {
            console.error("Dashboard error:", err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [user, refreshing]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- GRAFİK AYARLARI (HORIZONUI STYLE) ---
    const lineChartOptions = {
        chart: { toolbar: { show: false }, dropShadow: { enabled: true, top: 13, left: 0, blur: 10, opacity: 0.1, color: "#4318FF" } },
        colors: ["#4318FF"],
        markers: { size: 0, colors: ["#4318FF"], strokeColors: "#fff", strokeWidth: 2, hover: { size: 7 } },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { categories: chartData.categories, labels: { style: { colors: "#A3AED0", fontSize: "12px", fontWeight: "500" } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { show: false },
        grid: { show: false },
        dataLabels: { enabled: false },
        tooltip: { theme: "dark", y: { formatter: (val) => `${val} İşlem` } },
    };

    const donutChartOptions = {
        labels: statusData.labels,
        colors: ["#4318FF", "#6AD2FF"],
        chart: { width: "100%" },
        states: { hover: { filter: { type: "none" } } },
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: "70%" } } },
        tooltip: { theme: "dark" },
    };

    if (loading) return <DashboardSkeleton />;

    return (
        <div className="mt-5 flex w-full flex-col gap-5 pb-10 animate-fadeIn">

            {/* 1. ÜST BİLGİ VE YENİLEME */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-2xl font-bold text-navy-700 dark:text-white">Merhaba, {user?.name} 👋</h3>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Bugün randevularına ve performansına bir bakalım.</p>
                </div>
                <button
                    onClick={() => { setRefreshing(true); fetchData(); }}
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white dark:bg-navy-800 text-brand-500 shadow-sm border border-gray-100 dark:border-white/10 hover:bg-gray-50 active:scale-95 transition-all"
                >
                    <MdRefresh className={`text-xl ${refreshing ? "animate-spin" : ""}`} />
                </button>
            </div>

            {/* 2. STAT KARTLARI (WIDGETS - PARA ÇIKARTILDI) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="flex items-center gap-4 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-400/10 text-brand-500"><MdPendingActions className="text-3xl" /></div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Bekleyen</p>
                        <h4 className="text-xl font-black text-navy-700 dark:text-white">{stats.upcomingCount} İşlem</h4>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-50 dark:bg-green-400/10 text-green-500"><MdCheckCircle className="text-2xl" /></div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Tamamlanan</p>
                        <h4 className="text-xl font-black text-navy-700 dark:text-white">{stats.completedCount} İşlem</h4>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-400/10 text-blue-500"><MdCalendarMonth className="text-2xl" /></div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Toplam Kayıt</p>
                        <h4 className="text-xl font-black text-navy-700 dark:text-white">{stats.totalAppointments}</h4>
                    </div>
                </div>
                <div className="flex items-center gap-4 bg-white dark:bg-navy-800 p-4 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-400/10 text-amber-500"><MdStar className="text-2xl" /></div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Ortalama Puan</p>
                        <h4 className="text-xl font-black text-navy-700 dark:text-white">{stats.avgRating.toFixed(1)} / 5.0</h4>
                    </div>
                </div>
            </div>

            {/* 3. ANA GRAFİKLER */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* İşlem Çizgi Grafiği */}
                <div className="lg:col-span-2 bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5">
                    <div className="flex items-center justify-between mb-6">
                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Aylık İşlem Performansı</h5>
                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 dark:bg-navy-900 rounded-lg text-xs font-bold text-brand-500">
                            <MdAccessTime /> Son 7 Ay
                        </div>
                    </div>
                    <div className="h-64">
                        <Chart options={lineChartOptions} series={chartData.series} type="line" width="100%" height="100%" />
                    </div>
                </div>

                {/* Dağılım Donut Grafiği */}
                <div className="bg-white dark:bg-navy-800 p-6 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 flex flex-col items-center">
                    <h5 className="text-lg font-bold text-navy-700 dark:text-white self-start mb-6">İşlem Dağılımı</h5>
                    <div className="relative h-48 w-full flex items-center justify-center">
                        <Chart options={donutChartOptions} series={statusData.series} type="donut" width="100%" height="100%" />
                        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span className="text-2xl font-black text-navy-700 dark:text-white">{stats.totalAppointments}</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase">Toplam</span>
                        </div>
                    </div>
                    <div className="mt-8 flex w-full justify-around gap-4 px-4">
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-2 w-2 rounded-full bg-brand-500" />
                                <span className="text-xs font-bold text-gray-400 uppercase">Gelecek</span>
                            </div>
                            <span className="text-sm font-black text-navy-700 dark:text-white">{statusData.series[0]}</span>
                        </div>
                        <div className="h-10 w-px bg-gray-100 dark:bg-white/10" />
                        <div className="flex flex-col items-center">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-2 w-2 rounded-full bg-[#6AD2FF]" />
                                <span className="text-xs font-bold text-gray-400 uppercase">Tamamlanan</span>
                            </div>
                            <span className="text-sm font-black text-navy-700 dark:text-white">{statusData.series[1]}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* 4. HIZLI İŞLEMLER */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Link to="/vault/adisyon" className="group flex flex-col items-center justify-center p-6 bg-white dark:bg-navy-800 rounded-[24px] border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                    <div className="h-12 w-12 rounded-2xl bg-brand-50 dark:bg-brand-400/10 text-brand-500 flex items-center justify-center text-2xl group-hover:bg-brand-500 group-hover:text-white transition-colors mb-3"><MdAddShoppingCart /></div>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">Adisyon Aç</span>
                </Link>
                <Link to="/vault/newcustomer" className="group flex flex-col items-center justify-center p-6 bg-white dark:bg-navy-800 rounded-[24px] border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                    <div className="h-12 w-12 rounded-2xl bg-green-50 dark:bg-green-400/10 text-green-500 flex items-center justify-center text-2xl group-hover:bg-green-500 group-hover:text-white transition-colors mb-3"><MdPersonAdd /></div>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">Yeni Müşteri</span>
                </Link>
                <Link to="/vault/crm" className="group flex flex-col items-center justify-center p-6 bg-white dark:bg-navy-800 rounded-[24px] border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                    <div className="h-12 w-12 rounded-2xl bg-blue-50 dark:bg-blue-400/10 text-blue-500 flex items-center justify-center text-2xl group-hover:bg-blue-500 group-hover:text-white transition-colors mb-3"><MdPeople /></div>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">CRM Kartları</span>
                </Link>
                <Link to="/staff/history" className="group flex flex-col items-center justify-center p-6 bg-white dark:bg-navy-800 rounded-[24px] border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all">
                    <div className="h-12 w-12 rounded-2xl bg-amber-50 dark:bg-amber-400/10 text-amber-500 flex items-center justify-center text-2xl group-hover:bg-amber-500 group-hover:text-white transition-colors mb-3"><MdHistory /></div>
                    <span className="text-sm font-bold text-navy-700 dark:text-white">İşlem Geçmişi</span>
                </Link>
            </div>

            {/* 5. TABLOLAR (RANDEVULAR VE YORUMLAR) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                {/* Son Randevular */}
                <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-gray-50 dark:border-white/5">
                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Yaklaşan Randevular</h5>
                        <Link to="/staff/upcoming" className="text-xs font-bold text-brand-500 hover:underline flex items-center gap-1">Tümünü Gör <MdChevronRight /></Link>
                    </div>
                    <div className="p-0 overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50 dark:bg-navy-900/50">
                            <tr>
                                <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Müşteri</th>
                                <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">İşlem</th>
                                <th className="p-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Zaman</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                            {recentAppointments.length > 0 ? recentAppointments.map((app) => (
                                <tr key={app.id} className="hover:bg-gray-50/50 dark:hover:bg-navy-900/20 transition-colors">
                                    <td className="p-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-navy-700 dark:text-white">{app.customer?.name} {app.customer?.surname}</span>
                                            <span className="text-[10px] font-medium text-gray-400">{app.customer?.phone}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex items-center gap-2">
                                            <MdContentCut className="text-brand-500" />
                                            <span className="text-xs font-bold text-navy-700 dark:text-white">{app.service?.name}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className="text-xs font-black text-navy-700 dark:text-white">{app.appointment_time.substring(0, 5)}</span>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                          {new Date(app.appointment_date).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
                        </span>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="3" className="p-8 text-center text-sm font-medium text-gray-400 italic">Planlanmış randevu bulunmuyor.</td></tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Son Değerlendirmeler */}
                <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-gray-50 dark:border-white/5">
                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Değerlendirmeler (Anonim)</h5>
                        <Link to="/staff/reviews" className="text-xs font-bold text-brand-500 hover:underline flex items-center gap-1">Tümünü Gör <MdChevronRight /></Link>
                    </div>
                    <div className="flex flex-col divide-y divide-gray-50 dark:divide-white/5">
                        {recentReviews.length > 0 ? recentReviews.map((rev) => (
                            <div key={rev.id} className="p-4 hover:bg-gray-50/50 dark:hover:bg-navy-900/20 transition-colors">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex flex-col">
                                        <span className="text-xs font-bold text-navy-700 dark:text-white">{rev.service}</span>
                                        <span className="text-[10px] font-medium text-gray-400 italic">
                      {new Date(rev.date).toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
                    </span>
                                    </div>
                                    <StarRating rating={rev.score} />
                                </div>
                                {rev.comment && (
                                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 line-clamp-2 italic bg-gray-50/50 dark:bg-navy-900/50 p-2 rounded-lg border border-gray-100 dark:border-white/5">
                                        "{rev.comment}"
                                    </p>
                                )}
                            </div>
                        )) : (
                            <div className="p-10 text-center flex flex-col items-center opacity-40">
                                <MdStar className="text-4xl mb-2" />
                                <p className="text-sm font-bold">Henüz yorum yapılmamış.</p>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default StaffDashboard;